import React, { useEffect, useRef, useState, useId, useCallback } from 'react';

const TOOLS = [
  { key: 'Scroll',      icon: 'swap_vert',      label: 'Coupes' },
  { key: 'ZoomAndPan',  icon: 'zoom_in',        label: 'Zoom/Pan' },
  { key: 'WindowLevel', icon: 'brightness_6',   label: 'Luminosité' },
];

const DCMViewer = ({ urls }) => {
  const containerRef = useRef(null);
  const appRef = useRef(null);
  const wheelHandlerRef = useRef(null);
  const activeToolRef = useRef('Scroll');
  const uid = useId().replace(/:/g, '');
  const divId = `dwvGroup${uid}`;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTool, setActiveTool] = useState('Scroll');
  const [sliceInfo, setSliceInfo] = useState(null);

  const switchTool = useCallback((tool) => {
    if (!appRef.current) return;
    try {
      appRef.current.setTool(tool);
      activeToolRef.current = tool;
      setActiveTool(tool);
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!urls?.length || !containerRef.current) return;

    let app;

    import('dwv').then(({ App, AppOptions, ViewConfig, WindowLevel }) => {
      app = new App();
      appRef.current = app;

      const viewConfig = new ViewConfig(divId);
      const options = new AppOptions({ dataViewConfigs: { '*': [viewConfig] } });
      options.viewOnFirstLoadItem = true;
      options.tools = {
        WindowLevel: {},
        ZoomAndPan: {},
        Scroll: {},
      };

      app.init(options);

      app.addEventListener('load', (e) => {
        try {
          const vc = new ViewConfig(divId);
          app.addDataViewConfig(e.dataid, vc, true);
        } catch (_) {}
      });

      app.addEventListener('positionchange', (e) => {
        try {
          const vals = e?.value?.[0];
          if (!vals) return;
          const idx = vals.length > 3 ? vals[3] : vals[2];
          setSliceInfo(prev => ({ ...prev, current: idx + 1 }));
        } catch (_) {}
      });

      app.addEventListener('loadend', () => {
        try {
          app.setTool('Scroll');
          const dataIds = app.getDataIds?.() ?? [];
          if (dataIds.length > 0) {
            const img = app.getImage(dataIds[0]);
            const size = img?.getGeometry?.().getSize?.();
            if (size) {
              // Detect first navigable dimension (dim2=Z for series, dim3=T for multi-frame)
              let navDim = null;
              for (let d = 2; d <= 3; d++) {
                const v = size.get(d);
                if (v != null && v > 1) { navDim = d; break; }
              }
              setSliceInfo({ current: 1, total: navDim ? size.get(navDim) : 1 });

              if (containerRef.current) {
                const handler = (ev) => {
                  const tool = activeToolRef.current;

                  if (tool === 'Scroll' && navDim && navDim !== 2) {
                    // DWV's wheel navigates Z (dim2) natively; drive other dims manually.
                    ev.preventDefault();
                    const ph = appRef.current?.getActiveLayerGroup?.()?.getPositionHelper?.();
                    if (!ph) return;
                    if (ev.deltaY > 0) ph.decrementPosition(navDim);
                    else ph.incrementPosition(navDim);
                  } else if (tool === 'WindowLevel') {
                    ev.preventDefault();
                    const vc = appRef.current
                      ?.getActiveLayerGroup?.()
                      ?.getActiveViewLayer?.()
                      ?.getViewController?.();
                    if (!vc) return;
                    const wl = vc.getWindowLevel?.();
                    if (!wl) return;
                    const step = Math.round(wl.width * 0.05) || 20;
                    vc.setWindowLevel(new WindowLevel(
                      wl.center + (ev.deltaY > 0 ? step : -step),
                      wl.width
                    ));
                  }
                };
                wheelHandlerRef.current = handler;
                containerRef.current.addEventListener('wheel', handler, { passive: false });
              }
            }
          }
        } catch (_) {}
        setLoading(false);
      });

      app.addEventListener('error', (e) => {
        console.error('[DCMViewer] error:', e?.detail || e);
        setError('Impossible de charger le fichier DICOM.');
        setLoading(false);
      });

      app.loadURLs(urls);
    }).catch((err) => {
      console.error('[DCMViewer] import error:', err);
      setError('Viewer DICOM non disponible.');
      setLoading(false);
    });

    return () => {
      if (wheelHandlerRef.current && containerRef.current) {
        containerRef.current.removeEventListener('wheel', wheelHandlerRef.current);
        wheelHandlerRef.current = null;
      }
      if (appRef.current) {
        appRef.current.reset();
        appRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urls?.join(','), divId]);

  return (
    <div className="dcm-viewer-wrapper">
      {loading && !error && (
        <div className="viewer-overlay">
          <div className="spinner"></div>
          <p>Chargement DICOM...</p>
        </div>
      )}
      {error && (
        <div className="viewer-overlay viewer-error">
          <i className="material-icons">medical_services</i>
          <p>{error}</p>
        </div>
      )}
      {!loading && !error && (
        <div className="dcm-toolbar">
          {TOOLS.map(({ key, icon, label }) => (
            <button
              key={key}
              className={`dcm-tool-btn ${activeTool === key ? 'active' : ''}`}
              title={label}
              onClick={() => switchTool(key)}
            >
              <i className="material-icons">{icon}</i>
              <span>{label}</span>
            </button>
          ))}
          {sliceInfo?.total > 1 && (
            <span className="dcm-slice-counter">
              {sliceInfo.current} / {sliceInfo.total}
            </span>
          )}
        </div>
      )}
      <div id={divId} ref={containerRef} className="dcm-layer-group" />
    </div>
  );
};

export default DCMViewer;
